import { ReactNode } from 'react';
import { AspectRatio, Box, Image, useColorModeValue } from '@chakra-ui/react';

interface Props {
  /** Object URL of the live picture; `null` shows `children` instead. */
  imageUrl: string | null;
  alt: string;
  /** What the frame says while there is no picture (starting, ended, …). */
  children?: ReactNode;
}

/**
 * The aim view's picture: the same fixed 16:9 frame as `CameraSnapshot`, so
 * nothing jumps between "starting", "live" and "ended" — but as wide as the
 * screen, and never taller than it (a phone held in landscape still sees the
 * whole picture without scrolling).
 *
 * `objectFit="contain"`, not `cover`: aiming is about the EDGES of the picture
 * (both baselines in frame), so nothing may be cropped. And nothing is drawn
 * over the picture — no grid, no guide (product decision for the MVP).
 */
export default function CameraAimFrame({ imageUrl, alt, children }: Props) {
  const frameBg = useColorModeValue('gray.100', 'whiteAlpha.100');
  const mutedText = useColorModeValue('gray.500', 'gray.400');

  return (
    <AspectRatio
      ratio={16 / 9}
      w="100%"
      maxW="calc((100vh - 96px) * 16 / 9)"
      mx="auto"
      borderRadius="md"
      overflow="hidden"
      bg={frameBg}
      data-testid="camera-aim-frame"
      data-state={imageUrl ? 'image' : 'placeholder'}
    >
      {imageUrl ? (
        <Image src={imageUrl} alt={alt} w="100%" h="100%" objectFit="contain" />
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          px={4}
          color={mutedText}
        >
          {children}
        </Box>
      )}
    </AspectRatio>
  );
}
